'use client'

import { useState, useEffect, useCallback } from 'react'
import { useTranslations } from 'next-intl'
import { GlobalSidebar } from '@/components/ui/global-sidebar'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Search, Plus, FolderTree } from 'lucide-react'
import { getAuthHeaders } from '@/lib/api-utils'
import { useToast } from '@/hooks/use-toast'
import { useProjects, EnhancedProject } from '@/hooks/use-projects'

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

// Helper function to get all child project IDs recursively
function getAllChildProjectIds(projects: EnhancedProject[], parentId: number): number[] {
  const childIds: number[] = []
  
  function findAndTraverse(projectList: EnhancedProject[]) {
    for (const project of projectList) {
      if (project.id === parentId && project.children) {
        const collectChildren = (children: EnhancedProject[]) => {
          for (const child of children) {
            childIds.push(child.id)
            if (child.children && child.children.length > 0) {
              collectChildren(child.children)
            }
          }
        }
        collectChildren(project.children)
        return
      }
      if (project.children && project.children.length > 0) {
        findAndTraverse(project.children)
      }
    }
  }
  
  findAndTraverse(projects)
  return childIds
}

interface Issue {
  id: number
  subject: string
  tracker: {
    id: number
    name: string
  }
  status: {
    id: number
    name: string
    is_closed: boolean
  }
  priority: {
    id: number
    name: string
  }
  project: {
    id: number
    name: string
  }
  estimated_hours?: number
  done_ratio?: number
}

interface AddTaskToSprintModalProps {
  isOpen: boolean
  onClose: () => void
  projectId: number
  sprintId: string
  onSuccess?: () => void
  onCreateNewTask?: () => void
  /** Include tasks from child projects */
  includeChildProjects?: boolean
}

export function AddTaskToSprintModal({
  isOpen,
  onClose,
  projectId,
  sprintId,
  onSuccess,
  onCreateNewTask,
  includeChildProjects = true
}: AddTaskToSprintModalProps) {
  const t = useTranslations('sprintsPage')
  const [searchTerm, setSearchTerm] = useState('')
  const debouncedSearchTerm = useDebounce(searchTerm, 500) // 500ms debounce
  const [issues, setIssues] = useState<Issue[]>([])
  const [loading, setLoading] = useState(false)
  const [addedIds, setAddedIds] = useState<number[]>([])
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [isAdding, setIsAdding] = useState(false)
  const { toast } = useToast()
  
  // Get all projects to find child projects
  const { data: projectsData } = useProjects()
  
  // Calculate all project IDs (current + children) for searching
  const allProjectIds = (() => {
    if (!includeChildProjects || !projectsData?.projects) return [projectId]
    const childIds = getAllChildProjectIds(projectsData.projects, projectId)
    return [projectId, ...childIds]
  })()

  useEffect(() => {
    if (isOpen) {
      loadSprintIssues()
      setSearchTerm('')
      setSelectedIds([])
    }
  }, [isOpen])

  // Fetch issues when search term changes
  useEffect(() => {
    if (isOpen && debouncedSearchTerm) {
      fetchProjectIssues(debouncedSearchTerm)
    } else if (isOpen && !debouncedSearchTerm) {
      // Load initial set of issues when no search term
      fetchProjectIssues('')
    }
  }, [isOpen, debouncedSearchTerm, projectId])

  const loadSprintIssues = async () => {
    try {
      // Load sprint issues using database API
      const numericSprintId = parseInt(sprintId, 10)
      const res = await fetch(`/api/db/sprints/${numericSprintId}/issues`)
      if (!res.ok) throw new Error('Failed to load sprint issues')

      const { issues } = await res.json()
      // issues array contains objects with 'id' field from database query
      const issueIds = issues.map((issue: { id: number }) => issue.id)
      setAddedIds(issueIds)
    } catch (error) {
      console.error('Error loading sprint issues:', error)
    }
  }

  const fetchProjectIssues = async (search: string) => {
    setLoading(true)
    console.log('[AddTaskModal] Searching for:', search, 'in projects:', allProjectIds)
    try {
      const authHeaders = getAuthHeaders()
      if (!authHeaders) {
        console.error('[AddTaskModal] No auth headers available')
        setIssues([])
        setLoading(false)
        return
      }
      
      // If search term looks like an issue ID (numeric or #123), fetch by ID
      const issueIdMatch = search.trim().match(/^#?(\d+)$/)
      if (issueIdMatch) {
        const issueId = issueIdMatch[1]
        console.log('[AddTaskModal] Detected issue ID search:', issueId)
        try {
          // First try direct issue fetch (most reliable)
          const directResponse = await fetch(`/api/issues/${issueId}`, { headers: authHeaders })
          console.log('[AddTaskModal] Direct fetch response:', directResponse.status)
          if (directResponse.ok) {
            const data = await directResponse.json()
            const issue = data.issue
            console.log('[AddTaskModal] Found issue:', issue)
            // Check if issue belongs to relevant projects (parent + children)
            if (issue && allProjectIds.includes(issue.project.id)) {
              setIssues([issue])
            } else if (issue) {
              // Show the issue anyway with a note that it's from a different project
              console.log('[AddTaskModal] Issue from different project, showing anyway')
              setIssues([issue])
            } else {
              setIssues([])
            }
          } else {
            console.log('[AddTaskModal] Direct fetch failed, trying search endpoint')
            // If direct fetch fails, try searching with issue_id parameter
            const searchResponse = await fetch(`/api/issues?issue_id=${issueId}&status_id=*`, { headers: authHeaders })
            if (searchResponse.ok) {
              const data = await searchResponse.json()
              const foundIssues = data.issues || []
              console.log('[AddTaskModal] Found issues via search:', foundIssues.length)
              setIssues(foundIssues)
            } else {
              console.log('[AddTaskModal] Search also failed')
              setIssues([])
            }
          }
        } catch (err) {
          console.error('[AddTaskModal] Error searching by issue ID:', err)
          setIssues([])
        }
        setLoading(false)
        return
      }
      
      // Search across all projects (parent + children) by subject
      console.log('[AddTaskModal] Text search across projects:', allProjectIds)
      const allIssues: Issue[] = []
      const fetchPromises = []
      
      for (const projId of allProjectIds) {
        let url: string
        
        if (search && search.trim()) {
          // Use the search parameter for better matching - searches subject and description
          // Use status_id=* to search all statuses (including closed)
          url = `/api/issues?project_id=${projId}&status_id=*&limit=100&search=${encodeURIComponent(search.trim())}`
        } else {
          // Load recent open issues when no search term
          url = `/api/issues?project_id=${projId}&status_id=open&limit=50&sort=id:desc`
        }
        
        console.log('[AddTaskModal] Fetching from:', url)
        const fetchPromise = fetch(url, { headers: authHeaders })
          .then(response => {
            if (response.ok) {
              return response.json()
            }
            console.log('[AddTaskModal] Failed to fetch from project', projId, response.status)
            return { issues: [] }
          })
          .then(data => {
            const projectIssues = data.issues || []
            console.log(`[AddTaskModal] Project ${projId} returned ${projectIssues.length} issues`)
            allIssues.push(...projectIssues)
          })
          .catch(err => {
            console.error(`[AddTaskModal] Error fetching issues for project ${projId}:`, err)
          })
        
        fetchPromises.push(fetchPromise)
      }
      
      // Wait for all project queries to complete
      await Promise.all(fetchPromises)
      
      // Remove duplicates (in case an issue appears in multiple results)
      const uniqueIssues = Array.from(
        new Map(allIssues.map(issue => [issue.id, issue])).values()
      )
      
      console.log('[AddTaskModal] Total unique issues found:', uniqueIssues.length)
      
      // Sort by ID descending (most recent first)
      uniqueIssues.sort((a, b) => b.id - a.id)
      
      // Limit total results
      setIssues(uniqueIssues.slice(0, 100))
    } catch (err) {
      console.error('Error fetching issues:', err)
      setIssues([])
    } finally {
      setLoading(false)
    }
  }

  const handleToggleTask = (issueId: number) => {
    if (addedIds.includes(issueId)) return // Don't allow selecting already added tasks
    
    setSelectedIds(prev => 
      prev.includes(issueId) 
        ? prev.filter(id => id !== issueId)
        : [...prev, issueId]
    )
  }

  const handleAddSelectedTasks = async () => {
    if (selectedIds.length === 0) return

    setIsAdding(true)
    try {
      const numericSprintId = parseInt(sprintId, 10)

      // POST to API route which handles authentication via JWT cookie
      const res = await fetch(`/api/db/sprints/${numericSprintId}/issues`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ issueIds: selectedIds })
      })

      if (!res.ok) {
        const error = await res.json()
        throw new Error(error.error || 'Failed to add tasks to sprint')
      }

      toast({
        title: 'Success',
        description: `${selectedIds.length} task${selectedIds.length > 1 ? 's' : ''} added to sprint`
      })

      setSelectedIds([])
      onSuccess?.()
      onClose()
    } catch (error) {
      console.error('Error adding tasks to sprint:', error)
      toast({
        title: 'Error',
        description: 'Failed to add tasks to sprint',
        variant: 'destructive'
      })
    } finally {
      setIsAdding(false)
    }
  }

  // Show all issues, including already added ones (but disabled)
  const displayIssues = issues

  return (
    <GlobalSidebar
      isOpen={isOpen}
      onClose={onClose}
      title={t('addTasksToSprint')}
      subtitle={t('addTasksSubtitle')}
      icon={<Plus className="h-5 w-5" />}
      width="md"
      footer={
        <div className="space-y-3">
          {selectedIds.length > 0 && (
            <div className="flex items-center justify-between gap-3 pb-3 border-b">
              <div className="text-sm text-muted-foreground">
                {selectedIds.length} task{selectedIds.length !== 1 ? 's' : ''} selected
              </div>
              <Button 
                onClick={handleAddSelectedTasks} 
                disabled={isAdding}
                size="sm"
              >
                {isAdding ? 'Adding...' : `Add ${selectedIds.length || ''} Task${selectedIds.length !== 1 ? 's' : ''}`}
              </Button>
            </div>
          )}
          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => {
              onCreateNewTask?.()
            }}
          >
            <Plus className="h-4 w-4 mr-2" />
            Create New Task
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Search - Always at top */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <Input
            type="text"
            placeholder={t('tasks.searchTasksPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            autoFocus
          />
        </div>

        {/* Simple List */}
        <div className="space-y-1">
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex items-center gap-3 p-3">
                  <Skeleton className="h-4 w-4" />
                  <Skeleton className="h-5 flex-1" />
                </div>
              ))}
            </div>
          ) : displayIssues.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">
                {searchTerm ? 'No tasks found matching your search' : 'No available tasks'}
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {displayIssues.map((issue) => {
                const isSelected = selectedIds.includes(issue.id)
                const isAlreadyAdded = addedIds.includes(issue.id)

                return (
                  <div
                    key={issue.id}
                    className={`flex items-center gap-3 py-3 transition-colors group ${
                      isAlreadyAdded 
                        ? 'opacity-60 cursor-not-allowed' 
                        : 'hover:bg-muted/30 cursor-pointer'
                    }`}
                    onClick={() => !isAlreadyAdded && handleToggleTask(issue.id)}
                  >
                    <div onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={isSelected}
                        disabled={isAlreadyAdded}
                        onCheckedChange={(checked) => {
                          if (!isAlreadyAdded) {
                            handleToggleTask(issue.id)
                          }
                        }}
                      />
                    </div>
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className="text-sm font-mono text-muted-foreground shrink-0">
                        #{issue.id}
                      </span>
                      <Badge variant="outline" className="text-xs shrink-0">
                        {issue.status.name}
                      </Badge>
                      {isAlreadyAdded && (
                        <Badge variant="secondary" className="text-xs shrink-0">
                          Already in sprint
                        </Badge>
                      )}
                      {/* Show child project indicator */}
                      {issue.project?.id !== projectId && (
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded shrink-0">
                          <FolderTree className="h-3 w-3" />
                          {issue.project?.name}
                        </span>
                      )}
                      <span className="text-sm truncate">
                        {issue.subject}
                      </span>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </GlobalSidebar>
  )
}
